var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useMemo, useEffect } from 'react';
import { Steps, Button, message, Row, Col } from 'antd';
import { Scenarios } from '@whatsper/texterchat-common';
import { observer } from 'mobx-react';
import Step1_General from './Step1_General';
import Step2_Triggers from './Step2_Triggers';
import Step_DataLoaders from './Step_DataLoaders';
import Step_Conditions from './Step_Conditions';
import Step_Actions from './Step_Actions';
import Step_Loops from './Step_Loops';
import { Trans, useTranslation } from 'react-i18next';
import { SCENARIO_THEME } from './scenarioTheme';
import styles from './ScenarioDraftWizard.module.scss';
const ScenarioDraftWizard = ({ draft, onSave, onCancel, loading, scenariosStore, onDataChange, setSaveTrigger, }) => {
    var _a, _b, _c;
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const normalizedDraft = useMemo(() => {
        const normalized = Object.assign({}, draft);
        if (normalized.loaders) {
            if (normalized.loaders.beforeConditions) {
                normalized.loaders.beforeConditions = normalized.loaders.beforeConditions.map((loader) => {
                    var _a;
                    return (Object.assign(Object.assign({}, loader), { confidentialData: (_a = loader.confidentialData) !== null && _a !== void 0 ? _a : false }));
                });
            }
            if (normalized.loaders.afterConditions) {
                normalized.loaders.afterConditions = normalized.loaders.afterConditions.map((loader) => {
                    var _a;
                    return (Object.assign(Object.assign({}, loader), { confidentialData: (_a = loader.confidentialData) !== null && _a !== void 0 ? _a : false }));
                });
            }
        }
        if (normalized.conditions) {
            normalized.conditions = normalized.conditions.map((group) => group.map((condition) => {
                var _a;
                return (Object.assign(Object.assign({}, condition), { confidentialData: (_a = condition.confidentialData) !== null && _a !== void 0 ? _a : false }));
            }));
        }
        if (normalized.actions) {
            normalized.actions = normalized.actions.map((action) => {
                var _a;
                return (Object.assign(Object.assign({}, action), { confidentialData: (_a = action.confidentialData) !== null && _a !== void 0 ? _a : false }));
            });
        }
        if (!normalized.loops) {
            normalized.loops = [];
        }
        return normalized;
    }, [draft]);
    const [formData, setFormData] = useState(normalizedDraft);
    React.useEffect(() => {
        if (onDataChange) {
            onDataChange(formData);
        }
    }, [formData, onDataChange]);
    const handleFormDataChange = (changedData) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), changedData)));
    };
    const handleLoadersChange = (position, newLoaders) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { loaders: Object.assign(Object.assign({}, prev.loaders), { [position === 'before' ? 'beforeConditions' : 'afterConditions']: newLoaders }) })));
    };
    const handleConditionsChange = (newConditions) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { conditions: newConditions })));
    };
    const handleActionsChange = (newActions) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { actions: newActions })));
    };
    const handleActionsOrderChange = (isOrdered) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { options: Object.assign(Object.assign({}, prev.options), { unorderedActions: !isOrdered }) })));
    };
    const handleLoopsChange = (position, newLoops) => {
        setFormData((prev) => {
            const otherLoops = (prev.loops || []).filter((l) => l.position !== position);
            return Object.assign(Object.assign({}, prev), { loops: [...otherLoops, ...newLoops] });
        });
    };
    const availableEvents = useMemo(() => {
        return Object.values(scenariosStore.events).flat();
    }, [scenariosStore.events]);
    const triggerProviderNames = useMemo(() => {
        const triggerDefs = availableEvents.filter((e) => { var _a; return (_a = formData.triggerEvents) === null || _a === void 0 ? void 0 : _a.includes(e.name); });
        return triggerDefs.flatMap((t) => t.dataProvidersNames || []);
    }, [formData.triggerEvents, availableEvents]);
    const allLoaderAliases = useMemo(() => {
        var _a, _b;
        const before = ((_a = formData.loaders) === null || _a === void 0 ? void 0 : _a.beforeConditions) || [];
        const after = ((_b = formData.loaders) === null || _b === void 0 ? void 0 : _b.afterConditions) || [];
        return [...before, ...after].map((l) => l.alias);
    }, [formData.loaders]);
    const getForeachAliases = (loops) => (loops || [])
        .filter((l) => l.loop.type === Scenarios.Components.LoopType.foreach)
        .map((l) => l.loop.as);
    const allLoopAliases = useMemo(() => {
        return getForeachAliases(formData.loops);
    }, [formData.loops]);
    const allUsedProviderNames = useMemo(() => {
        return [...allLoaderAliases, ...allLoopAliases];
    }, [allLoaderAliases, allLoopAliases]);
    // Loop aliases grouped by position
    const afterEventsLoopAliases = useMemo(() => {
        return getForeachAliases((formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.afterEvents));
    }, [formData.loops]);
    const beforeConditionsLoopAliases = useMemo(() => {
        return getForeachAliases((formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.beforeConditions));
    }, [formData.loops]);
    const afterConditionsLoopAliases = useMemo(() => {
        return getForeachAliases((formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.afterConditions));
    }, [formData.loops]);
    const beforeActionsLoopAliases = useMemo(() => {
        return getForeachAliases((formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.beforeActions));
    }, [formData.loops]);
    const providersForPreConditions = useMemo(() => {
        return [...triggerProviderNames, ...afterEventsLoopAliases];
    }, [triggerProviderNames, afterEventsLoopAliases]);
    const providersForPostConditions = useMemo(() => {
        var _a;
        const beforeLoaderAliases = (((_a = formData.loaders) === null || _a === void 0 ? void 0 : _a.beforeConditions) || []).map((l) => l.alias);
        return [...triggerProviderNames, ...beforeLoaderAliases, ...afterEventsLoopAliases, ...beforeConditionsLoopAliases];
    }, [triggerProviderNames, formData.loaders, afterEventsLoopAliases, beforeConditionsLoopAliases]);
    const providersForActions = useMemo(() => {
        var _a, _b;
        const beforeLoaderAliases = (((_a = formData.loaders) === null || _a === void 0 ? void 0 : _a.beforeConditions) || []).map((l) => l.alias);
        const afterLoaderAliases = (((_b = formData.loaders) === null || _b === void 0 ? void 0 : _b.afterConditions) || []).map((l) => l.alias);
        return [
            ...triggerProviderNames,
            ...beforeLoaderAliases,
            ...afterLoaderAliases,
            ...afterEventsLoopAliases,
            ...beforeConditionsLoopAliases,
            ...afterConditionsLoopAliases,
            ...beforeActionsLoopAliases,
        ];
    }, [triggerProviderNames, formData.loaders, afterEventsLoopAliases, beforeConditionsLoopAliases, afterConditionsLoopAliases, beforeActionsLoopAliases]);
    const availableLoaders = useMemo(() => {
        return Object.values(scenariosStore.loaders).flat();
    }, [scenariosStore.loaders]);
    const availableConditions = useMemo(() => {
        return Object.values(scenariosStore.conditions).flat();
    }, [scenariosStore.conditions]);
    const availableActions = useMemo(() => {
        return Object.values(scenariosStore.actions).flat();
    }, [scenariosStore.actions]);
    const beforeLoaderAliases = useMemo(() => {
        var _a;
        return (((_a = formData.loaders) === null || _a === void 0 ? void 0 : _a.beforeConditions) || []).map((l) => l.alias);
    }, [formData.loaders]);
    const afterConditionLoaderAliases = useMemo(() => {
        var _a;
        return (((_a = formData.loaders) === null || _a === void 0 ? void 0 : _a.afterConditions) || []).map((l) => l.alias);
    }, [formData.loaders]);
    const steps = [
        {
            title: SCENARIO_THEME.general.name,
            description: t('stepDescriptionGeneral'),
            icon: React.createElement(SCENARIO_THEME.general.icon, {
                style: { color: SCENARIO_THEME.general.color },
            }),
            content: React.createElement(Step1_General, { formData: formData, setFormData: handleFormDataChange }),
        },
        {
            title: SCENARIO_THEME.triggers.name,
            description: t('stepDescriptionTriggers'),
            icon: React.createElement(SCENARIO_THEME.triggers.icon, {
                style: { color: SCENARIO_THEME.triggers.color },
            }),
            content: (React.createElement(Step2_Triggers, { formData: formData, setFormData: handleFormDataChange, availableEvents: availableEvents })),
        },
        {
            title: t('stepTitleLoopsPostEvents'),
            description: t('stepDescriptionLoopsPostEvents'),
            icon: React.createElement(SCENARIO_THEME.loops.icon, { style: { color: SCENARIO_THEME.loops.color } }),
            content: (React.createElement(Step_Loops, { loops: (formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.afterEvents), onLoopsChange: (newLoops) => handleLoopsChange(Scenarios.Components.LoopPosition.afterEvents, newLoops), availableProviders: triggerProviderNames, position: Scenarios.Components.LoopPosition.afterEvents, allUsedNames: allUsedProviderNames })),
        },
        {
            title: t('stepTitleLoadersPreCond'),
            description: t('stepDescriptionLoadersPreCond'),
            icon: React.createElement(SCENARIO_THEME.loadersPreCondition.icon, {
                style: { color: SCENARIO_THEME.loadersPreCondition.color },
            }),
            content: (React.createElement(Step_DataLoaders, { formData: ((_a = formData.loaders) === null || _a === void 0 ? void 0 : _a.beforeConditions) || [], setFormData: (newList) => handleLoadersChange('before', newList), availableLoaders: availableLoaders, position: "before", allLoaderAliases: allLoaderAliases, availableProviders: providersForPreConditions })),
        },
        {
            title: t('stepTitleLoopsPreCond'),
            description: t('stepDescriptionLoopsPreCond'),
            icon: React.createElement(SCENARIO_THEME.loops.icon, { style: { color: SCENARIO_THEME.loops.color } }),
            content: (React.createElement(Step_Loops, { loops: (formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.beforeConditions), onLoopsChange: (newLoops) => handleLoopsChange(Scenarios.Components.LoopPosition.beforeConditions, newLoops), availableProviders: [...triggerProviderNames, ...beforeLoaderAliases, ...afterEventsLoopAliases], position: Scenarios.Components.LoopPosition.beforeConditions, allUsedNames: allUsedProviderNames })),
        },
        {
            title: SCENARIO_THEME.conditions.name,
            description: t('stepDescriptionConditions'),
            icon: React.createElement(SCENARIO_THEME.conditions.icon, {
                style: { color: SCENARIO_THEME.conditions.color },
            }),
            content: (React.createElement(Step_Conditions, { formData: formData.conditions || [[]], setFormData: handleConditionsChange, availableConditions: availableConditions, availableProviders: providersForPostConditions })),
        },
        {
            title: t('stepTitleLoopsPostCond'),
            description: t('stepDescriptionLoopsPostCond'),
            icon: React.createElement(SCENARIO_THEME.loops.icon, { style: { color: SCENARIO_THEME.loops.color } }),
            content: (React.createElement(Step_Loops, { loops: (formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.afterConditions), onLoopsChange: (newLoops) => handleLoopsChange(Scenarios.Components.LoopPosition.afterConditions, newLoops), availableProviders: [...triggerProviderNames, ...beforeLoaderAliases, ...afterEventsLoopAliases, ...beforeConditionsLoopAliases], position: Scenarios.Components.LoopPosition.afterConditions, allUsedNames: allUsedProviderNames })),
        },
        {
            title: t('stepTitleLoadersPostCond'),
            description: t('stepDescriptionLoadersPostCond'),
            icon: React.createElement(SCENARIO_THEME.loadersPostCondition.icon, {
                style: { color: SCENARIO_THEME.loadersPostCondition.color },
            }),
            content: (React.createElement(Step_DataLoaders, { formData: ((_b = formData.loaders) === null || _b === void 0 ? void 0 : _b.afterConditions) || [], setFormData: (newList) => handleLoadersChange('after', newList), availableLoaders: availableLoaders, position: "after", allLoaderAliases: allLoaderAliases, availableProviders: providersForPostConditions })),
        },
        {
            title: t('stepTitleLoopsPreActions'),
            description: t('stepDescriptionLoopsPreActions'),
            icon: React.createElement(SCENARIO_THEME.loops.icon, { style: { color: SCENARIO_THEME.loops.color } }),
            content: (React.createElement(Step_Loops, { loops: (formData.loops || []).filter((l) => l.position === Scenarios.Components.LoopPosition.beforeActions), onLoopsChange: (newLoops) => handleLoopsChange(Scenarios.Components.LoopPosition.beforeActions, newLoops), availableProviders: [...triggerProviderNames, ...beforeLoaderAliases, ...afterEventsLoopAliases, ...beforeConditionsLoopAliases, ...afterConditionLoaderAliases, ...afterConditionsLoopAliases], position: Scenarios.Components.LoopPosition.beforeActions, allUsedNames: allUsedProviderNames })),
        },
        {
            title: SCENARIO_THEME.actions.name,
            description: t('stepDescriptionActions'),
            icon: React.createElement(SCENARIO_THEME.actions.icon, {
                style: { color: SCENARIO_THEME.actions.color },
            }),
            content: (React.createElement(Step_Actions, { list: formData.actions || [], onListChange: handleActionsChange, isOrdered: !((_c = formData.options) === null || _c === void 0 ? void 0 : _c.unorderedActions), onOrderChange: handleActionsOrderChange, availableActions: availableActions, availableProviders: providersForActions })),
        },
    ];
    const handleNext = () => {
        if (currentStep === 1 && (!formData.triggerEvents || formData.triggerEvents.length === 0)) {
            message.error(t('wizardMustHaveTrigger'));
            return;
        }
        setCurrentStep(currentStep + 1);
    };
    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };
    const handleSave = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        const getDiffByValue = (oldArr, newArr) => {
            const oldSet = new Set(oldArr);
            const newSet = new Set(newArr);
            const added = newArr.filter((x) => !oldSet.has(x));
            const removed = oldArr.filter((x) => !newSet.has(x));
            return { added, removed };
        };
        const getIndexedDiff = (oldArr, newArr) => {
            const oldStrings = oldArr.map((item) => JSON.stringify(item));
            const newStrings = newArr.map((item) => JSON.stringify(item));
            const removed = [];
            const newStringsCopy = [...newStrings];
            oldStrings.forEach((oldItemStr, index) => {
                const matchIndexInNew = newStringsCopy.indexOf(oldItemStr);
                if (matchIndexInNew !== -1) {
                    newStringsCopy.splice(matchIndexInNew, 1);
                }
                else {
                    removed.push({ index });
                }
            });
            const added = newStringsCopy.map((str) => JSON.parse(str));
            return { added, removed };
        };
        if (!formData.triggerEvents || formData.triggerEvents.length === 0) {
            message.error(React.createElement(Trans, { i18nKey: "scenarioMustHaveAtLeastOneTriggerEvent" }));
            setCurrentStep(1);
            return;
        }
        if (!formData.actions || formData.actions.length === 0) {
            message.error(React.createElement(Trans, { i18nKey: "scenarioMustHaveAtLeastOneAction" }));
            setCurrentStep(9);
            return;
        }
        const operations = [];
        const originalDraft = draft;
        const newDraftData = formData;
        if (newDraftData.name !== originalDraft.name) {
            operations.push({ rename: newDraftData.name });
        }
        if (newDraftData.description !== originalDraft.description) {
            operations.push({ changeDescription: newDraftData.description || '' });
        }
        const originalTriggers = originalDraft.triggerEvents || [];
        const newTriggers = newDraftData.triggerEvents || [];
        const hasChanged = (key) => JSON.stringify(originalDraft[key] || null) !== JSON.stringify(newDraftData[key] || null);
        if (hasChanged('triggerEvents')) {
            const { added, removed } = getDiffByValue(originalTriggers, newTriggers);
            added.forEach((t) => operations.push({ addTriggerEvent: t }));
            removed.forEach((r) => operations.push({ removeTriggerEvent: r }));
        }
        if (hasChanged('loaders')) {
            const getLoadersDiff = (original, current, position) => {
                const { added, removed } = getIndexedDiff(original || [], current || []);
                // Order is important: remove operations must be applied before add operations.
                // Also remove from highest index first to avoid index shifting issues.
                removed.sort((a, b) => b.index - a.index);
                removed.forEach((r) => operations.push({ removeLoader: { index: r.index, position } }));
                added.forEach((l) => operations.push({ addLoader: Object.assign(Object.assign({}, l), { position }) }));
            };
            getLoadersDiff((_a = originalDraft.loaders) === null || _a === void 0 ? void 0 : _a.beforeConditions, (_b = newDraftData.loaders) === null || _b === void 0 ? void 0 : _b.beforeConditions, 'beforeConditions');
            getLoadersDiff((_c = originalDraft.loaders) === null || _c === void 0 ? void 0 : _c.afterConditions, (_d = newDraftData.loaders) === null || _d === void 0 ? void 0 : _d.afterConditions, 'afterConditions');
        }
        if (hasChanged('conditions')) {
            const oldGroups = originalDraft.conditions || [];
            const newGroups = newDraftData.conditions || [];
            const maxGroups = Math.max(oldGroups.length, newGroups.length);
            for (let groupIndex = 0; groupIndex < maxGroups; groupIndex++) {
                const oldGroup = oldGroups[groupIndex] || [];
                const newGroup = newGroups[groupIndex] || [];
                if (JSON.stringify(oldGroup) === JSON.stringify(newGroup)) {
                    continue;
                }
                const { added, removed } = getIndexedDiff(oldGroup, newGroup);
                removed.sort((a, b) => b.index - a.index);
                removed.forEach(r => {
                    operations.push({ removeCondition: { groupIndex, index: r.index } });
                });
                added.forEach(condition => {
                    operations.push({ addCondition: Object.assign(Object.assign({}, condition), { groupIndex }) });
                });
            }
        }
        if (hasChanged('actions')) {
            const oldActions = originalDraft.actions || [];
            const newActions = newDraftData.actions || [];
            const { added, removed } = getIndexedDiff(oldActions, newActions);
            added.forEach((action) => operations.push({ addAction: action }));
            removed.forEach((r) => operations.push({ removeAction: { index: r.index } }));
        }
        if (((_e = originalDraft.options) === null || _e === void 0 ? void 0 : _e.unorderedActions) !== ((_f = newDraftData.options) === null || _f === void 0 ? void 0 : _f.unorderedActions)) {
            operations.push({ setUnorderedActions: !!((_g = newDraftData.options) === null || _g === void 0 ? void 0 : _g.unorderedActions) });
        }
        if (hasChanged('loops')) {
            const oldAllLoops = originalDraft.loops || [];
            const loopPositions = [
                Scenarios.Components.LoopPosition.afterEvents,
                Scenarios.Components.LoopPosition.beforeConditions,
                Scenarios.Components.LoopPosition.afterConditions,
                Scenarios.Components.LoopPosition.beforeActions,
            ];
            const loopAdds = [];
            const loopRemoves = [];
            loopPositions.forEach((position) => {
                const oldPos = oldAllLoops.filter((l) => l.position === position);
                const newPos = (newDraftData.loops || []).filter((l) => l.position === position);
                const { added, removed } = getIndexedDiff(oldPos, newPos);
                added.forEach((l) => loopAdds.push({ addLoop: { loop: l.loop, position } }));
                // Convert per-position index to global flat-array index
                removed.sort((a, b) => b.index - a.index);
                removed.forEach((r) => {
                    let positionCount = 0;
                    const globalIndex = oldAllLoops.findIndex((l) => {
                        if (l.position !== position)
                            return false;
                        if (positionCount === r.index)
                            return true;
                        positionCount++;
                        return false;
                    });
                    if (globalIndex !== -1) {
                        loopRemoves.push({ removeLoop: { index: globalIndex } });
                    }
                });
            });
            // Removals must be applied before additions to avoid duplicate name conflicts
            loopRemoves.forEach((op) => operations.push(op));
            loopAdds.forEach((op) => operations.push(op));
        }
        if (operations.length === 0) {
            message.info(t('wizardNoChanges'));
            onCancel();
            return;
        }
        yield onSave(draft, operations);
    }), [draft, formData, onSave]);
    useEffect(() => {
        if (setSaveTrigger) {
            setSaveTrigger(handleSave);
        }
        return () => {
            if (setSaveTrigger) {
                setSaveTrigger(null);
            }
        };
    }, [handleSave, setSaveTrigger]);
    return (React.createElement(Row, { gutter: 32 },
        React.createElement(Col, { span: 6 },
            React.createElement(Steps, { current: currentStep, direction: "vertical", onChange: setCurrentStep, items: steps, className: styles.stepsContainer })),
        React.createElement(Col, { span: 18 },
            React.createElement("div", { className: "steps-content", style: { minHeight: '300px', paddingBottom: '24px' } }, steps[currentStep].content),
            React.createElement("div", { className: "steps-action", style: { marginTop: '24px' } },
                currentStep > 0 && (React.createElement(Button, { style: { margin: '0 8px' }, onClick: () => handlePrev() },
                    React.createElement(Trans, { i18nKey: "wizardPrevious" }))),
                currentStep < steps.length - 1 && (React.createElement(Button, { type: "primary", onClick: () => handleNext() },
                    React.createElement(Trans, { i18nKey: "wizardNext" }))),
                currentStep === steps.length - 1 && (React.createElement(Button, { type: "primary", onClick: handleSave, loading: loading },
                    React.createElement(Trans, { i18nKey: "wizardSaveDraft" }))),
                React.createElement(Button, { onClick: onCancel, style: { marginLeft: 8 } },
                    React.createElement(Trans, { i18nKey: "wizardCancel" }))))));
};
export default observer(ScenarioDraftWizard);
